import { supabase } from '@/lib/supabase';

export interface ChatbotIssuePayload {
    issue_type: 'chatbot_issue';
    domain_id: string;
    session_id: string;
    conversation_link: string;
    /** Human-readable workspace, see `workspaceLabel`. */
    workspace?: string;
    /** Page the conversation started on, to reproduce it with the same skills. */
    page_url?: string;
    selected_message_content: string;
    user_message_content?: string;
    issue_subtype: 'accurate_but_not_ok' | 'not_accurate';
    expected_answer: string;
    reporter_name: string;
    reporter_email: string;
}

export interface OtherIssuePayload {
    issue_type: 'other';
    title: string;
    description: string;
    domain: string;
    reporter_name: string;
    reporter_email: string;
}

export type SupportTicketPayload = ChatbotIssuePayload | OtherIssuePayload;

export interface CreateSupportTicketResponse {
    success: boolean;
    /**
     * Linear's issue uuid — NOT `support_tickets.id`. That row is keyed by
     * `linear_issue_id`, so a foreign key pointing here must target
     * `support_tickets(linear_issue_id)`. Both are uuid, so the wrong target
     * type-checks and only fails at insert time.
     */
    issue_id: string | null;
    issue_identifier: string | null;
    issue_url: string | null;
    title: string | null;
    description: string | null;
    error: string | null;
}

export class SupportTicketError extends Error {
    status: number | null;
    constructor(message: string, status: number | null = null) {
        super(message);
        this.name = 'SupportTicketError';
        this.status = status;
    }
}

export function getSupportFunctionUrl(): string {
    const url = import.meta.env.VITE_SUPPORT_FUNCTION_URL?.trim();
    if (!url) {
        throw new SupportTicketError('VITE_SUPPORT_FUNCTION_URL is not configured');
    }
    return url;
}

export async function getSupabaseAccessToken(): Promise<string> {
    const {
        data: { session },
        error,
    } = await supabase.auth.getSession();
    if (error) {
        throw new SupportTicketError(`Failed to get session: ${error.message}`);
    }
    if (!session?.access_token) {
        throw new SupportTicketError('Not authenticated. Please sign in again.', 401);
    }
    return session.access_token;
}

/**
 * The Linear issue already filed under this conversation link, if any.
 *
 * `createSupportTicket` writes the local `support_tickets` row itself, before the
 * caller has a chance to store the returned issue id anywhere of its own. If that
 * caller-side write fails, this row is the only durable record that the ticket
 * exists — so callers that can be retried should look here first rather than
 * filing a second Linear issue for the same conversation.
 *
 * Errors are propagated rather than swallowed: not knowing is not the same as
 * knowing there is no ticket, and guessing wrong files the duplicate this guard
 * exists to prevent.
 */
export async function findTicketIdByConversationLink(link: string): Promise<string | null> {
    const { data, error } = await supabase
        .from('support_tickets')
        .select('linear_issue_id')
        .eq('conversation_link', link)
        .limit(1)
        .maybeSingle();
    if (error) {
        throw new SupportTicketError(`Failed to check for an existing ticket: ${error.message}`);
    }
    return data?.linear_issue_id ?? null;
}

export async function createSupportTicket(
    payload: SupportTicketPayload
): Promise<CreateSupportTicketResponse> {
    const functionUrl = getSupportFunctionUrl();
    const accessToken = await getSupabaseAccessToken();

    let response: Response;
    try {
        response = await fetch(functionUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ payload }),
        });
    } catch (error) {
        const message = error instanceof Error ? error.message : 'Unknown network error';
        throw new SupportTicketError(`Network error: ${message}`);
    }

    if (!response.ok) {
        let detail: string | null = null;
        try {
            const body = (await response.json()) as { error?: string };
            detail = body.error ?? null;
        } catch {
            // ignore parse errors
        }
        const suffix = detail ? `: ${detail}` : '';
        throw new SupportTicketError(
            `Failed to create ticket (${response.status})${suffix}`,
            response.status
        );
    }

    let result: CreateSupportTicketResponse;
    try {
        result = (await response.json()) as CreateSupportTicketResponse;
    } catch {
        throw new SupportTicketError('Failed to parse response');
    }

    // Persist ticket reference in Supabase for the backoffice list
    if (result.success && result.issue_id) {
        const domain = payload.issue_type === 'chatbot_issue' ? payload.domain_id : payload.domain;
        const conversationLink =
            payload.issue_type === 'chatbot_issue' ? payload.conversation_link : null;

        const { error: insertError } = await supabase.from('support_tickets').insert({
            linear_issue_id: result.issue_id,
            linear_identifier: result.issue_identifier ?? '',
            linear_url: result.issue_url,
            title: result.title ?? '',
            description: result.description ?? '',
            conversation_link: conversationLink,
            issue_type: payload.issue_type,
            domain,
            reporter_email: payload.reporter_email,
            reporter_name: payload.reporter_name,
        });

        if (insertError) {
            throw new SupportTicketError(
                `Ticket created in Linear (${result.issue_identifier}) but failed to save locally: ${insertError.message}`
            );
        }
    }

    return result;
}
